import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { Toaster } from "./components/ui/sonner";
import usePageTitle from "./hooks/usePageTitle";
import { PortalNoindex } from "./seo/Seo";

// Public marketing site
import Home from "./pages/public/Home";
import Wholesale from "./pages/public/Wholesale";
import PublicMenu from "./pages/public/Menu";
import Catering from "./pages/public/Catering";
import WhereToFindUs from "./pages/public/WhereToFindUs";
import About from "./pages/public/About";
import Contact from "./pages/public/Contact";
import NotFound from "./pages/public/NotFound";
import ResetPassword from "./pages/ResetPassword";

// Client portal
import ClientLayout from "./layouts/ClientLayout";
import ClientLogin from "./pages/client/Login";
import Dashboard from "./pages/client/DashboardNew";
import Shop from "./pages/client/Shop";
import Checkout from "./pages/client/Checkout";
import Orders from "./pages/client/Orders";
import RecurringOrders from "./pages/client/RecurringOrders";
import Invoices from "./pages/client/Invoices";
import Messages from "./pages/client/Messages";
import Reports from "./pages/client/Reports";
import Profile from "./pages/client/Profile";
import Team from "./pages/client/Team";
import HelpCenter from "./pages/client/HelpCenter";
import PaymentMethods from "./pages/client/PaymentMethods";

// Admin portal
import AdminLayout from "./layouts/AdminLayout";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import AdminOrders from "./pages/admin/Orders";
import AdminRecurringOrders from "./pages/admin/RecurringOrders";
import AdminClients from "./pages/admin/Clients";
import AdminMessages from "./pages/admin/Messages";
import AdminUsers from "./pages/admin/Users";
import AdminSecurity from "./pages/admin/Security";
import AdminClientTypes from "./pages/admin/ClientTypes";
import AdminInvoices from "./pages/admin/Invoices";
import AdminDocumentTemplates from "./pages/admin/DocumentTemplates";
import AdminHelpCenter from "./pages/admin/HelpCenter";
import AdminNotificationSettings from "./pages/admin/NotificationSettings";
import AdminReports from "./pages/admin/Reports";
import AdminPickupDiscrepancies from "./pages/admin/reports/PickupDiscrepancies";
import AdminRevenueReport from "./pages/admin/reports/RevenueReport";
import AdminClientPerformance from "./pages/admin/reports/ClientPerformance";
import AdminProductCosts from "./pages/admin/ProductCosts";
import AdminOrderInsights from "./pages/admin/OrderInsights";
import AdminOverOrdering from "./pages/admin/OverOrdering";
import AdminMenuCalendar from "./pages/admin/MenuCalendar";
import AdminCoverage from "./pages/admin/scheduling/Coverage";
import AdminScheduleBuilder from "./pages/admin/scheduling/Builder";
import AdminKitchenSetup from "./pages/admin/scheduling/KitchenSetup";
import AdminSchedulingSettings from "./pages/admin/settings/Scheduling";
import AdminShiftRequests from "./pages/admin/scheduling/Requests";
import AdminInventory from "./pages/admin/Inventory";
import AdminInventoryNotifications from "./pages/admin/settings/InventoryNotifications";
import AdminOrderingDeadlines from "./pages/admin/settings/OrderingDeadlines";
import AdminTimezoneSettings from "./pages/admin/settings/TimezoneSettings";
import AdminOverdueReminders from "./pages/admin/settings/OverdueReminders";

// Staff portal
import StaffLayout from "./layouts/StaffLayout";
import StaffLogin from "./pages/staff/Login";
import StaffDashboard from "./pages/staff/Dashboard";
import StaffInventory from "./pages/staff/Inventory";
import StaffRecipes from "./pages/staff/Recipes";
import StaffProduction from "./pages/staff/Production";
import StaffDelivery from "./pages/staff/Delivery";
import StaffMenuCalendar from "./pages/staff/MenuCalendar";
import StaffSchedule from "./pages/staff/Schedule";
import StaffRequests from "./pages/staff/Requests";

// Component to handle page titles (must be inside Router)
function PageTitleHandler() {
  usePageTitle();
  return <PortalNoindex />;
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <PageTitleHandler />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/wholesale" element={<Wholesale />} />
            <Route path="/menu" element={<PublicMenu />} />
            <Route path="/catering" element={<Catering />} />
            <Route path="/where-to-find-us" element={<WhereToFindUs />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Client Portal Login */}
            <Route path="/client/login" element={<ClientLogin />} />
            
            {/* Client Portal (protected) - wrapped in CartProvider */}
            <Route path="/client" element={<CartProvider><ClientLayout /></CartProvider>}>
              <Route index element={<Navigate to="/client/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="shop" element={<Shop />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<Orders />} />
              <Route path="recurring-orders" element={<RecurringOrders />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="payment-methods" element={<PaymentMethods />} />
              <Route path="messages" element={<Messages />} />
              <Route path="reports" element={<Reports />} />
              <Route path="profile" element={<Profile />} />
              <Route path="team" element={<Team />} />
              <Route path="help" element={<HelpCenter />} />
            </Route>

            {/* Admin Portal Login */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Admin Portal (protected) */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="recurring-orders" element={<AdminRecurringOrders />} />
              <Route path="menu-calendar" element={<AdminMenuCalendar />} />
              <Route path="scheduling" element={<AdminCoverage />} />
              <Route path="scheduling/builder" element={<AdminScheduleBuilder />} />
              <Route path="scheduling/kitchens" element={<AdminKitchenSetup />} />
              <Route path="scheduling/requests" element={<AdminShiftRequests />} />
              <Route path="settings/scheduling" element={<AdminSchedulingSettings />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="settings/inventory-notifications" element={<AdminInventoryNotifications />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="client-types" element={<AdminClientTypes />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="security" element={<AdminSecurity />} />
              <Route path="documents" element={<AdminDocumentTemplates />} />
              <Route path="help" element={<AdminHelpCenter />} />
              <Route path="notification-settings" element={<AdminNotificationSettings />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="reports/pickup-discrepancies" element={<AdminPickupDiscrepancies />} />
              <Route path="reports/revenue" element={<AdminRevenueReport />} />
              <Route path="reports/client-performance" element={<AdminClientPerformance />} />
              <Route path="product-costs" element={<AdminProductCosts />} />
              <Route path="order-insights" element={<AdminOrderInsights />} />
              <Route path="over-ordering" element={<AdminOverOrdering />} />
              <Route path="settings/ordering-deadlines" element={<AdminOrderingDeadlines />} />
              <Route path="settings/timezone" element={<AdminTimezoneSettings />} />
              <Route path="settings/overdue-reminders" element={<AdminOverdueReminders />} />
            </Route>

            {/* Staff Portal Login */}
            <Route path="/staff/login" element={<StaffLogin />} />
            
            {/* Staff Portal (protected) */}
            <Route path="/staff" element={<StaffLayout />}>
              <Route index element={<Navigate to="/staff/dashboard" replace />} />
              <Route path="dashboard" element={<StaffDashboard />} />
              <Route path="menu-calendar" element={<StaffMenuCalendar />} />
              <Route path="schedule" element={<StaffSchedule />} />
              <Route path="requests" element={<StaffRequests />} />
              <Route path="inventory" element={<StaffInventory />} />
              <Route path="recipes" element={<StaffRecipes />} />
              <Route path="production" element={<StaffProduction />} />
              <Route path="delivery" element={<StaffDelivery />} />
            </Route>

            {/* Anything else: public 404 (noindex) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;
